import { InputContainer } from "@/components/ui/forms/inputContainer"
import { UserCell } from "@/components/ui/layout/table/userCell"
import { cn } from "@/modules/lib/utils"
import { type FieldValueChange } from "@/typings/formik"
import { displayRole } from "@modules/boxStaff/entities/boxStaffUtils"
import { BoxStaffStatus } from "@modules/boxStaff/gateway/boxStaffDto"
import { useBoxStaffs } from "@modules/boxStaff/hooks/useBoxStaff"
import { type FieldInputProps } from "formik"

type SearchStaffInputProps<T, K> = {
  label: string
  name: string
  isRequiredInput: boolean
  setFieldValue: FieldValueChange<K>
  getFieldProps: (nameOrOptions: T) => FieldInputProps<any>
  error?: React.JSX.Element
  className?: string
  filterByStatus?: BoxStaffStatus[]
} & React.ComponentProps<"div">

export function SearchStaffInput<T, K>({
  label,
  name,
  isRequiredInput,
  setFieldValue,
  getFieldProps,
  error,
  className,
  filterByStatus,
  ...props
}: SearchStaffInputProps<T, K>) {
  const { data } = useBoxStaffs({ pageSize: 50, page: 0, litsOfStatus: [BoxStaffStatus.ACCEPTED] })
  const filteredData = filterByStatus ? data.filter((staffPerson) => filterByStatus.includes(staffPerson.status)) : data
  const staffIdSelected = getFieldProps(name as T).value as string | undefined

  const selectedVariant = "border-2 border-borderPrimary"
  const neutralVariant = "border-1 border-borderDefault"
  const unselectedVariant = "border-1 border-borderDefault opacity-50"

  function handleClick(staffId: string) {
    setFieldValue(name, staffId)
  }

  return (
    <InputContainer
      isRequiredInput={isRequiredInput}
      label={label}
      name={name}
      error={error}
      className={cn("flex w-full", className)}
      {...props}
    >
      <div className="grid w-full grid-cols-2 gap-2">
        {filteredData.map((staffPerson) => (
          <UserCell
            key={staffPerson.id}
            member={{
              avatar: staffPerson.avatar,
              username: staffPerson.username,
              firstname: staffPerson.firstname,
              lastname: staffPerson.lastname,
            }}
            subtitle={displayRole(staffPerson.role)}
            className={cn(
              "flex cursor-pointer justify-center rounded-2xl px-2 py-3",
              !staffIdSelected ? neutralVariant : "",
              staffIdSelected === staffPerson.id ? selectedVariant : "",
              staffIdSelected && staffIdSelected !== staffPerson.id ? unselectedVariant : "",
            )}
            onClick={() => handleClick(staffPerson.id)}
          />
        ))}
      </div>
    </InputContainer>
  )
}
